import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { SeoService } from '@cnt-workspace/data-access';
import {
  FooterComponent,
  HomeLocationsComponent,
  MagneticBtnDirective,
  NavbarComponent,
  NetworkMapComponent,
  prefersReducedMotion,
  runWhenIdle,
} from '@cnt-workspace/ui';
import { HomeFaqComponent } from './components/home-faq/home-faq.component';
import { HomeFavoriteHostsComponent } from './components/home-favorite-hosts/home-favorite-hosts.component';
import { HomeHeroComponent } from './components/home-hero/home-hero.component';
import { HomeMasonryComponent } from './components/home-masonry/home-masonry.component';
import { HomeReviewsComponent } from './components/home-reviews/home-reviews.component';
import { HomeRoom2roamRailComponent } from './components/home-room2roam-rail/home-room2roam-rail.component';
import { TripPreviewMapComponent } from './components/trip-preview-map/trip-preview-map.component';

// P86 — gsap + ScrollTrigger are dynamic-imported inside runWhenIdle()
// below. Home's animations are all below-fold, so latency doesn't
// affect LCP. Now safe because home-hero handles its own CSS-first
// initial state (see P86 hero rework).
import type { gsap as GsapNS } from 'gsap';
import type { ScrollTrigger as ScrollTriggerType } from 'gsap/ScrollTrigger';

@Component({
  selector: 'cnt-home',
  standalone: true,
  imports: [
    RouterLink,
    NgOptimizedImage,
    FooterComponent,
    MagneticBtnDirective,
    HomeLocationsComponent,
    HomeFaqComponent,
    HomeMasonryComponent,
    HomeHeroComponent,
    NavbarComponent,
    TripPreviewMapComponent,
    NetworkMapComponent,
    HomeRoom2roamRailComponent,
    HomeFavoriteHostsComponent,
    HomeReviewsComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject<object>(PLATFORM_ID);
  private seo = inject(SeoService);
  private sanitizer = inject(DomSanitizer);

  private scrollTriggers: ScrollTriggerType[] = [];
  private appPhonesTween?: gsap.core.Tween;

  /** Populated inside runWhenIdle after dynamic import. */
  private gsap!: typeof GsapNS;
  private ST!: typeof ScrollTriggerType;

  isVideoOpen = false;
  videoUrl!: SafeResourceUrl;
  private readonly brandVideoEmbed = 'https://www.youtube.com/embed/vuisyx-U944?autoplay=1&rel=0';
  /** Plain watch URL — used as the user-visible fallback when the
   *  embedded iframe is blocked (corporate networks, tracking
   *  prevention, etc). */
  readonly brandVideoFallbackUrl = 'https://www.youtube.com/watch?v=vuisyx-U944';

  @ViewChild('videoCloseBtn') private videoCloseBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('videoFallbackLink') private videoFallbackLink?: ElementRef<HTMLAnchorElement>;
  /** Element that had focus before the modal opened — restored on close
   *  so keyboard users land back on the poster button they activated. */
  private lastFocusedElement: HTMLElement | null = null;

  openVideo(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.lastFocusedElement = document.activeElement as HTMLElement | null;
    }
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.brandVideoEmbed);
    this.isVideoOpen = true;
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.add('cnt-modal-open');
      // Wait for @if to render, then move focus into the dialog.
      queueMicrotask(() => this.videoCloseBtn?.nativeElement.focus());
    }
  }

  closeVideo(): void {
    this.isVideoOpen = false;
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('cnt-modal-open');
      // Return focus to the poster (or whatever originally opened us) so
      // keyboard users don't get dumped to <body>.
      this.lastFocusedElement?.focus();
      this.lastFocusedElement = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isVideoOpen) this.closeVideo();
  }

  /** Two-element focus cycle for the video modal (close button ↔ fallback
   *  link). Enough elements to justify a trap, few enough to skip a
   *  general implementation. */
  onVideoModalKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const close = this.videoCloseBtn?.nativeElement;
    const fallback = this.videoFallbackLink?.nativeElement;
    if (!close || !fallback) return;
    const active = document.activeElement;
    if (event.shiftKey && active === close) {
      event.preventDefault();
      fallback.focus();
    } else if (!event.shiftKey && active === fallback) {
      event.preventDefault();
      close.focus();
    }
  }

  ngOnInit(): void {
    this.seo.update({
      title: 'CurbNTurf | Find Your RV Spot — Private Land Camping & Stays',
      description:
        'Find unique private RV spots on farms, vineyards, and ranches across the US. Book directly with hosts. No membership fees. The RV Freedom Experience.',
      url: '/',
      image: 'assets/images/host_farm.webp',
      imageAlt: 'CurbNTurf — private land camping on farms, vineyards, and driveways',
      imageWidth: 640,
      imageHeight: 640,
    });
    // P49/C — WebSite + SearchAction so Google can render a sitelinks
    // search box for the brand. The urlTemplate points at /search?q=
    // which the SearchResultsComponent already hydrates into freeText.
    this.seo.setStructuredData({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'CurbNTurf',
      url: 'https://www.curbnturf.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: 'https://www.curbnturf.com/search?q={search_term_string}',
        },
        'query-input': 'required name=search_term_string',
      },
    });
  }

  // Canonical GSAP-lifecycle example: reduced-motion guard, SSR guard,
  // idle-deferred dynamic import, and the scrollTriggers[] kill array
  // in ngOnDestroy below. See docs/architecture.md's "GSAP / animation
  // conventions" section (in client/apps/cnt-landing/) — copy this
  // shape for any new scroll-driven animation.
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Skip every decorative animation when the OS reports reduced
    // motion — the global CSS rule in styles.scss already collapses
    // CSS transitions; this bails the JS-driven ScrollTriggers too.
    if (prefersReducedMotion()) return;

    // All home animations are below-the-fold ScrollTriggers. Defer
    // the setup to browser-idle so it doesn't compete with the LCP
    // paint. The user can't see anything we'd be animating yet.
    runWhenIdle(async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([import('gsap'), import('gsap/ScrollTrigger')]);
      this.gsap = gsap;
      this.ST = ScrollTrigger;
      this.gsap.registerPlugin(this.ST);
      this.initStickyScroll();
      this.initLineAnimations();
      this.initStaggeredCards();
      this.initHowItWorks();
      // Magnetic-button behavior now handled by MagneticBtnDirective
      this.initStatsCounters();
      this.initNetworkAnimatedSection();
      this.initCardPhysics();
      this.initAppDownloadSection();
      this.initCtaSection();

      // Debounced ScrollTrigger refresh on resize. The home page has
      // ~14 pinned/scrubbed ScrollTriggers; without an explicit
      // refresh on viewport changes, they hold the stale measurements
      // taken at first paint and the pinned sections drift when the
      // user rotates a tablet or resizes a desktop window.
      let resizeTimer: ReturnType<typeof setTimeout> | null = null;
      this.resizeHandler = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.ST.refresh(), 200);
      };
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    });
  }

  private resizeHandler?: () => void;

  // -----------------------------

  toggleMobileNav(): void {
    const navMenu = document.querySelector('.nav-menu');
    navMenu?.classList.toggle('is-open');
  }

  private initStickyScroll(): void {
    const sectionHeight = document.querySelector('.section-height') as HTMLElement;
    const trackFlex = document.querySelector('.track-flex') as HTMLElement;
    if (!sectionHeight || !trackFlex) return;

    const panelNames = ['one', 'two', 'three', 'four', 'five'];
    const root = document.documentElement;

    // Initialize SVG line offsets to hidden state
    panelNames.forEach((name) => {
      root.style.setProperty(`--panel_${name}_line_offset`, '-400%');
    });

    // Horizontal scroll: move .track left as user scrolls through .section-height
    const st = this.gsap.to('.track', {
      x: () => -(trackFlex.scrollWidth - window.innerWidth),
      ease: 'none',
      scrollTrigger: {
        trigger: '.section-height',
        scrub: 1,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self: { progress: number }) => {
          // Animate SVG line offsets panel by panel
          const progress = self.progress;
          panelNames.forEach((name, i) => {
            const panelStart = i / 5;
            const panelEnd = (i + 1) / 5;
            const local = Math.max(0, Math.min(1, (progress - panelStart) / (panelEnd - panelStart)));
            const offset = (1 - local) * -400;
            root.style.setProperty(`--panel_${name}_line_offset`, `${offset}%`);
          });
        },
      },
    });

    if (st.scrollTrigger) {
      this.scrollTriggers.push(st.scrollTrigger);
    }
  }

  private initLineAnimations(): void {
    const lines = document.querySelectorAll('.line_animation');
    lines.forEach((line) => {
      const st = this.ST.create({
        trigger: line,
        start: 'top 85%',
        once: true,
        onEnter: () => line.classList.add('is-visible'),
      });
      this.scrollTriggers.push(st);
    });
  }

  private initStaggeredCards(): void {
    const wrap = document.querySelector('.gsap-collections-wrap');
    if (!wrap) return;
    const cards = this.gsap.utils.toArray('.gsap-collection-card');

    // Bottom-up entrance reveal
    const st = this.gsap.from(cards, {
      y: 60,
      opacity: 0,
      duration: 1,
      stagger: 0.15,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: wrap,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });

    if (st.scrollTrigger) {
      this.scrollTriggers.push(st.scrollTrigger);
    }
  }

  private initHowItWorks(): void {
    const wrap = document.querySelector('.gsap-how-works-wrap');
    if (!wrap) return;
    const steps = this.gsap.utils.toArray('.gsap-how-step');

    // Slow, luxuriant cinematic fade sequence
    const stepsSt = this.gsap.from(steps, {
      y: 60,
      opacity: 0,
      duration: 1.2,
      stagger: 0.3,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: wrap,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });

    if (stepsSt.scrollTrigger) {
      this.scrollTriggers.push(stepsSt.scrollTrigger);
    }
  }

  private initStatsCounters(): void {
    const wrap = document.querySelector('.gsap-stats-wrap');
    if (!wrap) return;
    const stats = this.gsap.utils.toArray('.gsap-stat-number');

    stats.forEach((statEl) => {
      const stat = statEl as HTMLElement;
      const targetStr = stat.getAttribute('data-target');
      if (!targetStr) return;

      const targetVal = parseFloat(targetStr);
      const isInteger = targetVal % 1 === 0;
      const suffix = stat.getAttribute('data-suffix') || '';

      const proxy = { val: 0 };

      const st = this.ST.create({
        trigger: wrap,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          this.gsap.to(proxy, {
            val: targetVal,
            duration: 2,
            ease: 'power2.out',
            onUpdate: () => {
              const formatted = isInteger ? Math.round(proxy.val).toString() : proxy.val.toFixed(1);
              stat.innerHTML = formatted + suffix;
            },
          });
        },
      });
      this.scrollTriggers.push(st);
    });
  }

  private initNetworkAnimatedSection(): void {
    const container = document.querySelector('.network-text-container') as HTMLElement;
    const btnContainer = document.querySelector('.network-btn-container') as HTMLElement;
    if (!container) return;

    // Helper to safely split text nodes into words and chars without destroying HTML tags
    const splitTextNodes = (element: HTMLElement) => {
      const processNode = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (!text || !text.trim().length) return;

          const words = text.split(/(\s+)/); // keep spaces separate
          const fragment = document.createDocumentFragment();

          words.forEach((wordText) => {
            if (wordText.trim() === '') {
              fragment.appendChild(document.createTextNode(wordText));
            } else {
              const wordSpan = document.createElement('span');
              wordSpan.className = 'word inline-block whitespace-nowrap';

              const chars = wordText.split('');
              chars.forEach((char) => {
                const charSpan = document.createElement('span');
                charSpan.className = 'char inline-block opacity-0';
                // Use a starting transform for the blur reveal
                charSpan.style.transform = 'translateY(20px) scale(1.1)';
                charSpan.style.filter = 'blur(10px)';
                charSpan.textContent = char;
                wordSpan.appendChild(charSpan);
              });
              fragment.appendChild(wordSpan);
            }
          });
          node.parentNode?.replaceChild(fragment, node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          if (el.classList.contains('category-pill')) {
            el.classList.add('char', 'opacity-0');
            // Keep the pill's tilt through the reveal: an inline transform
            // replaces Tailwind's rotate class, so carry data-tilt into it.
            el.style.transform = `translateY(20px) scale(1.1) rotate(${Number(el.dataset['tilt'] ?? 0)}deg)`;
            el.style.filter = 'blur(10px)';
            // We consciously do NOT parse child nodes here so the entire pill pops in as one sequence step.
          } else if (el.classList.contains('material-symbols-outlined')) {
            // Apply character animation classes to icons so they stagger in sequence
            el.classList.add('char', 'inline-block', 'opacity-0');
            el.style.transform = 'translateY(20px) scale(1.1)';
            el.style.filter = 'blur(10px)';
          } else {
            Array.from(el.childNodes).forEach(processNode);
          }
        }
      };

      Array.from(element.childNodes).forEach(processNode);
    };

    // 1. Split the text
    splitTextNodes(container);

    // 2. Animate it
    const chars = container.querySelectorAll('.char');
    const st = this.ST.create({
      trigger: container,
      start: 'top 80%',
      onEnter: () => {
        this.gsap.to(chars, {
          y: 0,
          scale: 1,
          rotation: (_i: number, el: Element) => Number((el as HTMLElement).dataset['tilt'] ?? 0),
          opacity: 1,
          filter: 'blur(0px)',
          stagger: 0.015,
          duration: 1.2,
          ease: 'power3.out',
        });
        if (btnContainer) {
          this.gsap.to(btnContainer, {
            y: 0,
            opacity: 1,
            duration: 1,
            delay: 0.5,
            ease: 'power3.out',
          });
        }
      },
      once: true,
    });
    this.scrollTriggers.push(st);
  }

  private initAppDownloadSection(): void {
    const wrap = document.querySelector('.gsap-app-download-wrap');
    if (!wrap) return;

    // Reveal animation
    const stReveal = this.ST.create({
      trigger: wrap,
      start: 'top 80%',
      once: true,
      onEnter: () => {
        this.gsap.to('.gsap-app-left', { y: 0, opacity: 1, duration: 1, ease: 'power3.out' });
        this.gsap.to('.gsap-app-right', { y: 0, opacity: 1, duration: 1, delay: 0.2, ease: 'power3.out' });
      },
    });

    // Floating yoyo physics for phones. Created paused and toggled by a
    // second ScrollTrigger on the same wrap so the infinite repeat/yoyo
    // tween — and the RAF ticker it keeps alive — only runs while the
    // section is actually on screen, instead of for the component's
    // whole lifetime.
    this.appPhonesTween = this.gsap.to('.gsap-app-phones', {
      y: -15,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      paused: true,
    });
    const stPhones = this.ST.create({
      trigger: wrap,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => this.appPhonesTween?.play(),
      onEnterBack: () => this.appPhonesTween?.play(),
      onLeave: () => this.appPhonesTween?.pause(),
      onLeaveBack: () => this.appPhonesTween?.pause(),
    });

    this.scrollTriggers.push(stReveal, stPhones);
  }

  initCardPhysics() {
    setTimeout(() => {
      const scrollSection = document.querySelector('.horizontal-cards-section') as HTMLElement;
      const scrollWrap = document.querySelector('.exhibit-list-wrap') as HTMLElement;

      if (scrollSection && scrollWrap) {
        const introEl = document.querySelector('.card-stack-intro') as HTMLElement;
        const cardWrappers = this.gsap.utils.toArray('.exhibit-card-wrapper') as HTMLElement[];
        const scrollDistance = scrollWrap.scrollWidth + window.innerWidth;

        // Programmatically generate random motion per card so no two scrolls look alike
        const easePool = [
          'sine.inOut',
          'power1.inOut',
          'power2.inOut',
          'power3.inOut',
          'circ.inOut',
          'expo.out',
          'back.out(1.2)',
          'power1.out',
          'power2.out',
          'sine.out',
          'power3.out',
        ];
        const randEase = () => easePool[Math.floor(Math.random() * easePool.length)];
        const randBetween = (min: number, max: number) => min + Math.random() * (max - min);
        const randSign = () => (Math.random() > 0.5 ? 1 : -1);

        const variants = cardWrappers.map(() => {
          const xDrift = randBetween(30, 110) * randSign();
          const frameCount = 2 + Math.floor(Math.random() * 4); // 2–5 keyframes
          const motion = Array.from({ length: frameCount }, () => ({
            y: randBetween(25, 90) * randSign(),
            rotation: randBetween(3, 16) * randSign(),
            ease: randEase(),
          }));
          return { xDrift, motion };
        });

        const tl = this.gsap.timeline();
        const panPosition = 0;

        // Fade out intro headline steadily as cards begin scrolling over it
        if (introEl) {
          tl.to(introEl, { opacity: 0, ease: 'none', duration: 0.3 }, panPosition);
        }

        // Main wrapper pans all cards horizontally together
        tl.fromTo(
          scrollWrap,
          { x: () => window.innerWidth },
          { x: () => -(scrollWrap.scrollWidth + window.innerWidth * 0.2), ease: 'none', duration: 1 },
          panPosition,
        );

        // Reveal the nested Outro block as the cards slide off the left side
        const outroReveal = document.querySelector('.mo-outro-reveal') as HTMLElement;
        if (outroReveal) {
          // Fade it in earlier in the scroll scrub (while final cards are sweeping over it)
          tl.to(outroReveal, { opacity: 1, duration: 0.25, ease: 'power2.inOut' }, 0.55);
          // Enable inner pointer events for the button only when fully revealed
          tl.set(outroReveal, { pointerEvents: 'auto' }, 0.55);
        }

        // Layer per-card variance on top of the wrapper pan
        cardWrappers.forEach((card, i) => {
          const v = variants[i];
          if (!v) return;
          // X drift — constant speed offset (linear so it doesn't fight the wrapper ease)
          tl.to(card, { x: v.xDrift, ease: 'none', duration: 1 }, panPosition);
          // Combined y / rotation / scale path — fully unique per card
          tl.to(card, { keyframes: v.motion, duration: 1 }, panPosition);
        });

        // Give the outro text and button a moment to breathe, then overlap section slides up
        tl.fromTo(
          '.content-overlap-wrap',
          { marginTop: '0px' },
          { marginTop: () => `-${window.innerHeight}px`, ease: 'none', duration: 0.5 },
          1.2, // Starts at 1.2, which is slightly after the 1s card transition completes
        );

        const scrollPinSt = this.ST.create({
          trigger: scrollSection,
          pin: true,
          anticipatePin: 1,
          scrub: 1,
          start: 'top top',
          end: () => `+=${scrollDistance}`, // Standardized scrub distance
          animation: tl,
          invalidateOnRefresh: true,
        });
        this.scrollTriggers.push(scrollPinSt);
      }
    }, 100);
  }

  private initCtaSection(): void {
    const el = document.querySelector('.home-cta-inner');
    if (!el) return;
    const st = this.ST.create({
      trigger: el,
      start: 'top 82%',
      once: true,
      onEnter: () => {
        this.gsap.from('.home-cta-inner', { y: 32, opacity: 0, duration: 0.8, ease: 'power3.out' });
      },
    });
    this.scrollTriggers.push(st);
  }

  ngOnDestroy(): void {
    this.scrollTriggers.forEach((st) => st.kill());
    this.appPhonesTween?.kill();
    if (this.resizeHandler && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}
